
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Brain, MessageCircle, Send, Sparkles } from "lucide-react";

// Sample conversation
const initialConversation = [
  {
    role: "assistant",
    content: "Hello! I'm your AI business companion. How can I help you develop your business idea today?"
  }
];

// Sample saved ideas
const savedIdeas = [
  {
    id: 1,
    title: "Sustainable Fashion Marketplace",
    summary: "Platform connecting eco-conscious consumers with sustainable clothing brands"
  },
  {
    id: 2,
    title: "Virtual Team Building App",
    summary: "Remote team bonding activities and games for distributed teams"
  },
  {
    id: 3,
    title: "Local Chef Delivery Service",
    summary: "Connecting home chefs with local customers for authentic homemade meals"
  }
];

// Message component
const Message = ({ message }: { message: { role: string; content: string } }) => {
  const isUser = message.role === "user";
  
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div className={`max-w-[80%] rounded-lg p-4 ${isUser ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
        <p className="text-sm">{message.content}</p>
      </div>
    </div>
  );
};

// Saved idea card component
const SavedIdeaCard = ({ idea }: { idea: typeof savedIdeas[0] }) => {
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-md">{idea.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription>
          {idea.summary}
        </CardDescription>
      </CardContent>
      <CardFooter>
        <Button variant="ghost" size="sm">Continue</Button>
      </CardFooter>
    </Card>
  );
};

const AICompanion = () => {
  const [conversation, setConversation] = useState(initialConversation);
  const [message, setMessage] = useState("");
  
  const sendMessage = () => {
    if (!message.trim()) return;
    
    // Add user message to conversation
    const updatedConversation = [
      ...conversation,
      { role: "user", content: message }
    ];
    
    setConversation(updatedConversation);
    setMessage("");
    
    // Simulate AI response (in a real app, this would call an API)
    setTimeout(() => {
      setConversation([
        ...updatedConversation,
        { 
          role: "assistant", 
          content: "That's an interesting idea! Let me help you refine it. What problem are you trying to solve with this concept?" 
        }
      ]);
    }, 1000);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">AI Companion</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card className="h-[600px] flex flex-col">
            <CardHeader>
              <div className="flex items-center mb-1">
                <Brain className="h-5 w-5 mr-2 text-primary" />
                <CardTitle>Idea Development Assistant</CardTitle>
              </div>
              <CardDescription>
                Have a conversation with AI to refine your business ideas
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <div className="px-2">
                {conversation.map((msg, i) => (
                  <Message key={i} message={msg} />
                ))}
              </div>
            </CardContent>
            <CardFooter className="pt-4 border-t">
              <div className="flex w-full items-center space-x-2">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  className="min-h-[60px]"
                />
                <Button 
                  onClick={sendMessage} 
                  size="icon" 
                  className="h-[60px]"
                  disabled={!message.trim()}
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center mb-1">
                <Sparkles className="h-4 w-4 mr-2 text-primary" />
                <CardTitle className="text-lg">Idea Prompts</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm h-auto py-2 px-3"
                  onClick={() => {
                    setMessage("I have an idea for a subscription box service, but I'm not sure how to differentiate it.");
                  }}
                >
                  I have an idea for a subscription box service, but I'm not sure how to differentiate it.
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm h-auto py-2 px-3"
                  onClick={() => {
                    setMessage("What are some innovative business models in the sustainability space?");
                  }}
                >
                  What are some innovative business models in the sustainability space?
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm h-auto py-2 px-3"
                  onClick={() => {
                    setMessage("How can I validate my business idea without spending too much money?");
                  }}
                >
                  How can I validate my business idea without spending too much money?
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="flex items-center mb-1">
                <MessageCircle className="h-4 w-4 mr-2 text-primary" />
                <CardTitle className="text-lg">Saved Ideas</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {savedIdeas.map((idea) => (
                  <SavedIdeaCard key={idea.id} idea={idea} />
                ))}
                <Button variant="outline" className="w-full">
                  View All Saved Ideas
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AICompanion;
